import React, { useState, useEffect } from "react";
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Shuffle,
} from "lucide-react";
import { ImportedMeme } from "../types";
import { useCombinedMemes } from "../hooks/useCombinedMemes";
import { usePlausible } from "../hooks/usePlausible";
import OptimizedImage from "./OptimizedImage";

interface MemeTemplatePickerProps {
  onSelectTemplate: (meme: ImportedMeme) => void;
}

interface Category {
  name: string;
  count: number;
}

interface CombinedMeme {
  id: string;
  description: string;
  name: string;
  tags: string;
  url_path: string;
  thumb_path: string;
  rating: number;
  category: string;
  date: string;
  locale: string;
  example: string;
  mongo_id: string;
  version: number;
  local_image_path: string;
  local_thumb_path: string;
  created_at: string;
  updated_at: string;
  isCustom?: boolean;
}

// Category to Hebrew label mapping with order
const categoryLabels: Record<string, { label: string; order: number }> = {
  all: { label: "הכל", order: 0 },
  oz_and_tali: { label: "עוז וטלי", order: 0.5 },
  general: { label: "כללי", order: 1 },
  "all-time-popular": { label: "פופולרי בכל הזמנים", order: 2 },
  "daily-popular": { label: "פופולרי יומי", order: 3 },
  dank: { label: "דאנק", order: 3.5 },
  israeli: { label: "ישראלי", order: 4 },
  eretz_nehederet: { label: "ארץ נהדרת", order: 4.5 },
  pop: { label: "פופ", order: 5 },
  tv_abroad: { label: 'טלוויזיה מחו"ל', order: 6 },
  mashups: { label: "Mashups", order: 7 },
  classic: { label: "קלאסי", order: 8 },
  commercials: { label: "פרסומות", order: 9 },
  israeli_tv: { label: "טלוויזיה ישראלית", order: 10 },
  media: { label: "מדיה", order: 11 },
  parlament: { label: "הפרלמנט", order: 12 },
  asi_guri: { label: "אסי וגורי", order: 13 },
  jews: { label: "היהודים באים", order: 14 },
  standup: { label: "סטנד-אפ", order: 15 },
  goalstar: { label: "גולסטאר", order: 16 },
  gur_oach: { label: "גור ואוח", order: 17 },
  animals: { label: "חיות", order: 18 },
  kupa: { label: "קופה ראשית", order: 19 },
  pigamot: { label: "הפיג'מות", order: 20 },
  hazarot: { label: "חזרות", order: 21 },
  "new-memes": { label: "ממים חדשים", order: 22 },
  popular: { label: "פופולרי", order: 23 },
};

// Helper function to get Hebrew label for a category
const getCategoryLabel = (categoryName: string): string => {
  return categoryLabels[categoryName]?.label || categoryName;
};

// Helper function to get category order
const getCategoryOrder = (categoryName: string): number => {
  return categoryLabels[categoryName]?.order || 999;
};

const MemeTemplatePicker: React.FC<MemeTemplatePickerProps> = ({
  onSelectTemplate,
}) => {
  const {
    allMemes,
    shuffledMemes,
    loading,
    error,
    totalCount,
    loadedCount,
    reshuffle,
  } = useCombinedMemes();
  const { trackMemeSelected } = usePlausible();
  const [filteredMemes, setFilteredMemes] = useState<CombinedMeme[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] =
    useState<string>("oz_and_tali");
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [memesPerPage] = useState(12);

  useEffect(() => {
    if (allMemes.length > 0) {
      loadCategories();
    }
  }, [allMemes]);

  useEffect(() => {
    filterAndSortMemes();
  }, [shuffledMemes, selectedCategory, searchQuery]);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [selectedCategory, searchQuery]);

  const loadCategories = async () => {
    try {
      // Count memes per category from allMemes
      const categoryCounts: Record<string, number> = {};
      allMemes.forEach((meme) => {
        if (meme.category) {
          categoryCounts[meme.category] =
            (categoryCounts[meme.category] || 0) + 1;
        }
      });

      const categoryList = Object.entries(categoryCounts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => getCategoryOrder(a.name) - getCategoryOrder(b.name));

      setCategories(categoryList);
    } catch (err) {
      console.error("Error loading categories:", err);
    }
  };

  const filterAndSortMemes = () => {
    let filtered = [...shuffledMemes];

    // If there's a search query, search across all categories
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (meme) =>
          meme.description?.toLowerCase().includes(query) ||
          meme.name?.toLowerCase().includes(query) ||
          meme.tags?.toLowerCase().includes(query)
      );
    } else {
      // Only apply category filter when there's no search query
      if (selectedCategory !== "all") {
        filtered = filtered.filter(
          (meme) => meme.category === selectedCategory
        );
      }
    }

    setFilteredMemes(filtered);
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredMemes.length / memesPerPage);
  const startIndex = (currentPage - 1) * memesPerPage;
  const endIndex = startIndex + memesPerPage;
  const currentMemes = filteredMemes.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToPreviousPage = () => {
    goToPage(currentPage - 1);
  };

  const goToNextPage = () => {
    goToPage(currentPage + 1);
  };

  const handleSelectMeme = (meme: CombinedMeme) => {
    // Track the meme selection event
    trackMemeSelected(meme.id, meme.category, meme.isCustom);

    // Convert CombinedMeme to ImportedMeme format for compatibility
    // but preserve the isCustom property by casting to any
    const importedMeme: any = {
      id: meme.id,
      description: meme.description,
      name: meme.name,
      tags: meme.tags,
      url_path: meme.url_path,
      thumb_path: meme.thumb_path,
      rating: meme.rating,
      category: meme.category,
      date: meme.date,
      locale: meme.locale,
      example: meme.example,
      mongo_id: meme.mongo_id,
      version: meme.version,
      local_image_path: meme.local_image_path,
      local_thumb_path: meme.local_thumb_path,
      created_at: meme.created_at,
      updated_at: meme.updated_at,
      isCustom: meme.isCustom, // Preserve the isCustom property
    };
    onSelectTemplate(importedMeme);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleShuffle = () => {
    reshuffle();
    setCurrentPage(1); // Reset to first page when shuffling
  };

  if (loading) {
    return (
      <div className="w-full py-8">
        <div className="flex flex-col items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-lg">Loading memes...</span>
          {totalCount > 0 && (
            <div className="mt-4 text-sm text-gray-600">
              Loaded {loadedCount} of {totalCount} memes
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full py-8">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">Error</div>
          <p className="text-gray-600 mb-4">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        {/* Search and Category Filters */}
        <div className="space-y-4">
          {/* Search and Shuffle */}
          <div className="flex gap-3" dir="rtl">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="חפש ממים..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                dir="rtl"
              />
            </div>
            <button
              onClick={handleShuffle}
              className="flex items-center px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium whitespace-nowrap"
              dir="rtl"
            >
              <Shuffle className="w-4 h-4 ml-2" />
              ערבב
            </button>
          </div>

          {/* Category Filters */}
          <div>
            {/* Mobile: Dropdown */}
            <div className="sm:hidden">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <select
                  value={selectedCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full pl-10 pr-8 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white text-base"
                >
                  <option value="all">
                    {getCategoryLabel("all")} ({allMemes.length})
                  </option>
                  <option
                    value="oz_and_tali"
                    className="font-bold text-pink-600"
                  >
                    ✨ {getCategoryLabel("oz_and_tali")} ✨ (
                    {categories.find((cat) => cat.name === "oz_and_tali")
                      ?.count || 0}
                    )
                  </option>
                  {categories
                    .filter((cat) => cat.name !== "oz_and_tali")
                    .map((category) => (
                      <option key={category.name} value={category.name}>
                        {getCategoryLabel(category.name)} ({category.count})
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {/* Desktop: Category Badges */}
            <div className="hidden sm:block">
              <div className="flex flex-wrap-reverse gap-2 justify-center">
                {categories.map((category) => (
                  <button
                    key={category.name}
                    onClick={() => handleCategoryChange(category.name)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === category.name
                        ? category.name === "oz_and_tali"
                          ? "bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg shadow-pink-500/50"
                          : "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {category.name === "oz_and_tali" ? (
                      <>✨ {getCategoryLabel(category.name)} ✨</>
                    ) : (
                      getCategoryLabel(category.name)
                    )}
                  </button>
                ))}
                <button
                  onClick={() => handleCategoryChange("all")}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === "all"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {getCategoryLabel("all")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
          {currentMemes.map((meme) => (
            <div
              key={meme.id}
              onClick={() => handleSelectMeme(meme)}
              className={`cursor-pointer group hover:scale-105 transition-all duration-300 ${
                meme.isCustom
                  ? "hover:shadow-pink-500/50 hover:shadow-lg"
                  : "hover:shadow-lg"
              }`}
            >
              <div
                className={`relative rounded-lg overflow-hidden aspect-square ${"bg-gray-100"}`}
              >
                <OptimizedImage
                  src={meme.url_path}
                  thumbnailSrc={meme.thumb_path}
                  alt={meme.description || "Meme template"}
                  className="w-full h-full object-cover object-center"
                  style={{ width: "100%", height: "100%" }}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200" />
              </div>
              <div className="mt-2">
                <p
                  className={`text-xs sm:text-sm font-medium line-clamp-2 ${"text-gray-900"}`}
                >
                  {meme.description || meme.name || "Untitled"}
                </p>
                <p className={`text-xs ${"text-gray-500"}`}>{meme.category}</p>
              </div>
            </div>
          ))}
        </div>

        {filteredMemes.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-2">No memes found</div>
            <p className="text-gray-400">
              Try adjusting your search or category filter
            </p>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="mt-8">
            {/* Mobile: Simple Previous/Next with page indicator */}
            <div className="sm:hidden flex items-center justify-between">
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </button>

              <div className="text-sm text-gray-600 font-medium">
                Page {currentPage} of {totalPages}
              </div>

              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>

            {/* Desktop: Full pagination with page numbers */}
            <div className="hidden sm:flex items-center justify-center space-x-4">
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </button>

              <div className="flex items-center space-x-2">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? "bg-blue-600 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemeTemplatePicker;
